
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { ArrowRightIcon, PlayIcon } from '@heroicons/react/solid';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { useRef, useState } from 'react';
import WorkflowVisualization from '@/components/landing/WorkflowVisualization';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';
import { useUser } from '@/utils/useUser';
import { templateMetadata } from '@/lib/workflows/template-metadata';
const Faq = dynamic(() => import('@/components/Faq'));
// Features data
const features = [
    {
        title: 'Visual Node Editor',
        description: 'Drag, drop, and connect AI operations on an intuitive canvas. No coding required.',
        icon: (<svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z'/>
      </svg>),
    },
    {
        title: 'Parallel Processing',
        description: 'Run multiple AI operations simultaneously. Create faster, work smarter.',
        icon: (<svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 10V3L4 14h7v7l9-11h-7z'/>
      </svg>),
    },
    {
        title: 'Reusable Templates',
        description: 'Start with pre-built workflows or save your own for repeated use.',
        icon: (<svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'/>
      </svg>),
    },
    {
        title: 'Team Collaboration',
        description: 'Work together in real-time. Share workflows, co-create, and manage team projects seamlessly.',
        icon: (<svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'/>
      </svg>),
    },
    {
        title: 'Style Consistency',
        description: 'Use shared inputs across nodes to maintain uniform style across outputs.',
        icon: (<svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01'/>
      </svg>),
    },
    {
        title: 'All AI Models',
        description: 'Access image generation, editing, video creation, and audio in one workflow.',
        icon: (<svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z'/>
      </svg>),
    },
];
// Use cases
const useCases = [
    {
        title: 'Content Creators',
        description: 'Batch process videos for social media. Transform one idea into dozens of variations.',
        gradient: 'from-pink-500 via-rose-500 to-orange-400',
    },
    {
        title: 'E-commerce',
        description: 'Generate product videos, virtual try-ons, and lifestyle imagery at scale.',
        gradient: 'from-violet-500 via-purple-500 to-blue-500',
    },
    {
        title: 'Marketing Teams',
        description: 'Create consistent branded content across multiple campaigns simultaneously.',
        gradient: 'from-cyan-400 via-teal-500 to-emerald-500',
    },
    {
        title: 'Designers',
        description: 'Experiment with style transfers and variations without repetitive manual work.',
        gradient: 'from-amber-400 via-orange-500 to-red-500',
    },
];
// FAQs data
const faqs = [
    {
        q: 'What AI operations can I use in workflows?',
        a: 'You can use all our AI capabilities: text generation (GPT, Claude, Gemini, etc.), image generation (Nano Banana, Seedream, Midjourney, Flux, etc.), image editing, video generation (VEO, Kling, LTX, etc.), audio synthesis, and utility operations. Plus, collaborate with your team in real-time.',
    },
    {
        q: 'How do credits work with workflows?',
        a: 'Each node uses credits based on the operation type. For example, an Image to Image node uses the same credits as using the standalone tool.',
    },
    {
        q: 'Can I save and reuse my workflows?',
        a: 'Yes! All your workflows are automatically saved. You can also use our pre-built templates or create your own reusable templates.',
    },
    {
        q: 'Is there a limit to how many nodes I can use?',
        a: 'There is no hard limit on the number of nodes. However, very complex workflows with many parallel operations may take longer to process.',
    },
    {
        q: 'Can I use workflows on the Free plan?',
        a: 'AI Workflows is available on all paid plans. Free plan users can explore the workflow builder but need to upgrade to run workflows.',
    },
];
export default function AIWorkflowsPage() {
    const { user } = useUser();
    const { t } = useLocale('ai-workflows');
    const router = useRouter();
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const handleGetStarted = () => {
        if (user) {
            router.push('/workflows');
        }
        else {
            router.push('/login?redirect=/workflows');
        }
    };
    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
            },
        })),
    };
    return (<main className='min-h-screen bg-[#0a0a0f] text-white overflow-hidden'>
      <Seo templateTitle='AI Workflows - Visual Automation for AI' title='AI Workflows - Visual Automation for AI' description='Chain AI operations visually. Create images, transform them, generate videos, process text, and collaborate with your team—all in one automated workflow. No coding required.' image='https://easy-peasy.ai/api/og-template?title=AI%20Workflows'/>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
        }}/>
      </Head>

      {/* Custom styles for this page */}
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

        .font-outfit {
          font-family: 'Outfit', sans-serif;
        }

        .font-mono-jet {
          font-family: 'JetBrains Mono', monospace;
        }

        .gradient-text {
          background: linear-gradient(
            135deg,
            #a78bfa 0%,
            #ec4899 50%,
            #f97316 100%
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .glow-line {
          position: absolute;
          height: 2px;
          background: linear-gradient(
            90deg,
            transparent,
            #a78bfa,
            #ec4899,
            transparent
          );
          animation: glow-move 3s ease-in-out infinite;
        }

        @keyframes glow-move {
          0%,
          100% {
            opacity: 0.3;
            transform: translateX(-100%);
          }
          50% {
            opacity: 1;
            transform: translateX(100%);
          }
        }

        .node-pulse {
          animation: node-pulse 2s ease-in-out infinite;
        }

        @keyframes node-pulse {
          0%,
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.4);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 0 20px 10px rgba(167, 139, 250, 0);
          }
        }

        .floating {
          animation: floating 6s ease-in-out infinite;
        }

        @keyframes floating {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(2deg);
          }
        }

        .grid-bg {
          background-image:
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(
              90deg,
              rgba(255, 255, 255, 0.02) 1px,
              transparent 1px
            );
          background-size: 60px 60px;
        }

        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          opacity: 0.03;
        }

        .card-shine {
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.03) 50%,
            rgba(255, 255, 255, 0) 100%
          );
        }

        .connection-line {
          stroke-dasharray: 8 4;
          animation: dash 1s linear infinite;
        }

        @keyframes dash {
          to {
            stroke-dashoffset: -12;
          }
        }
      `}</style>

      {/* Noise overlay */}
      <div className='fixed inset-0 noise-overlay pointer-events-none z-50'/>

      {/* Grid background */}
      <div className='fixed inset-0 grid-bg pointer-events-none'/>

      {/* Gradient orbs - hidden on mobile for performance */}
      <div className='hidden md:block fixed top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-violet-600/20 blur-[120px] pointer-events-none'/>
      <div className='hidden md:block fixed top-[30%] right-[-15%] w-[500px] h-[500px] rounded-full bg-pink-600/15 blur-[100px] pointer-events-none'/>
      <div className='hidden md:block fixed bottom-[-10%] left-[30%] w-[400px] h-[400px] rounded-full bg-orange-500/10 blur-[80px] pointer-events-none'/>

      {/* Navigation */}
      <div className='relative z-40'>
        <LandingPageNavigation variant='light'/>
      </div>

      {/* Hero Section */}
      <section className='relative pt-32 pb-24 px-6 lg:px-8'>
        <div className='max-w-7xl mx-auto'>
          <div className='grid lg:grid-cols-2 gap-16 items-center'>
            {/* Left content */}
            <div className='font-outfit'>
              {/* Badge */}
              <div className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-8'>
                <span className='relative flex h-2 w-2'>
                  <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75'></span>
                  <span className='relative inline-flex rounded-full h-2 w-2 bg-emerald-500'></span>
                </span>
                <span className='text-sm text-gray-300 font-medium'>
                  {t('New Feature')}
                </span>
              </div>

              {/* Headline */}
              <h1 className='text-5xl lg:text-7xl font-bold leading-[1.1] mb-6'>
                <span className='text-white'>{t('Chain AI.')}</span>
                <br />
                <span className='gradient-text'>{t('Create Magic.')}</span>
              </h1>

              {/* Subheadline */}
              <p className='text-xl text-gray-400 leading-relaxed mb-10 max-w-xl'>
                {t('Visual automation for AI operations. Connect text generation, image creation, style transfer, video production, audio synthesis, and more—collaborate with your team in one powerful workflow.')}
              </p>

              {/* CTAs */}
              <div className='flex flex-wrap gap-4'>
                <button onClick={handleGetStarted} className='group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl font-semibold text-lg hover:from-violet-500 hover:to-pink-500 transition-all duration-300 shadow-lg shadow-violet-500/25'>
                  {t('Start Creating')}
                  <ArrowRightIcon className='w-5 h-5 group-hover:translate-x-1 transition-transform'/>
                </button>
                {/* <button
          onClick={() => {
            setIsVideoPlaying(true);
            videoRef.current?.play();
          }}
          className='inline-flex items-center gap-3 px-8 py-4 bg-white/5 border border-white/10 rounded-xl font-semibold text-lg hover:bg-white/10 transition-all duration-300'
        >
          <PlayIcon className='w-5 h-5' />
          {t('Watch Demo')}
        </button> */}
              </div>

              {/* Stats */}
              <div className='flex gap-12 mt-14 pt-8 border-t border-white/10'>
                <div>
                  <div className='text-3xl font-bold gradient-text'>10+</div>
                  <div className='text-sm text-gray-500 mt-1'>
                    {t('Node Types')}
                  </div>
                </div>
                <div>
                  <div className='text-3xl font-bold gradient-text'>∞</div>
                  <div className='text-sm text-gray-500 mt-1'>
                    {t('Possibilities')}
                  </div>
                </div>
                <div>
                  <div className='text-3xl font-bold gradient-text'>0</div>
                  <div className='text-sm text-gray-500 mt-1'>
                    {t('Code Required')}
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Animated workflow visualization */}
            <div className='relative h-[500px] lg:h-[600px] lg:col-span-1 lg:-mr-32 xl:-mr-48'>
              <WorkflowVisualization />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className='relative py-24 px-6 lg:px-8'>
        <div className='max-w-7xl mx-auto'>
          <div className='text-center mb-16 font-outfit'>
            <h2 className='text-4xl lg:text-5xl font-bold mb-4'>
              {t('How It')} <span className='gradient-text'>{t('Works')}</span>
            </h2>
            <p className='text-xl text-gray-400 max-w-2xl mx-auto'>
              {t('Three simple steps to automate your AI creations')}
            </p>
          </div>

          <div className='grid md:grid-cols-3 gap-8 items-stretch'>
            {[
            {
                step: '01',
                title: t('Add Nodes'),
                description: t('Drag AI operations onto the canvas. Text generation, image creation, video production, audio synthesis, and more.'),
                color: 'from-violet-500 to-purple-600',
            },
            {
                step: '02',
                title: t('Connect'),
                description: t('Draw connections between nodes. The output of one becomes the input of the next.'),
                color: 'from-pink-500 to-rose-600',
            },
            {
                step: '03',
                title: t('Run'),
                description: t('Hit run and watch your workflow execute. All operations process automatically.'),
                color: 'from-orange-400 to-amber-500',
            },
        ].map((item, index) => (<div key={index} className='relative group h-full'>
                <div className='absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl blur-xl' style={{
                background: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
            }}/>
                <div className='relative p-8 rounded-3xl bg-white/[0.03] border border-white/10 backdrop-blur-sm hover:border-white/20 transition-all duration-300 h-full flex flex-col'>
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} mb-6`}>
                    <span className='font-mono-jet text-xl font-bold text-white'>
                      {item.step}
                    </span>
                  </div>
                  <h3 className='text-2xl font-bold text-white mb-3 font-outfit'>
                    {item.title}
                  </h3>
                  <p className='text-gray-400 leading-relaxed'>
                    {item.description}
                  </p>
                </div>
              </div>))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className='relative py-24 px-6 lg:px-8 bg-gradient-to-b from-transparent via-violet-950/20 to-transparent'>
        <div className='max-w-7xl mx-auto'>
          <div className='text-center mb-16 font-outfit'>
            <h2 className='text-4xl lg:text-5xl font-bold mb-4'>
              {t('Powerful')}{' '}
              <span className='gradient-text'>{t('Features')}</span>
            </h2>
            <p className='text-xl text-gray-400 max-w-2xl mx-auto'>
              {t('Everything you need to create complex AI workflows')}
            </p>
          </div>

          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {features.map((feature, index) => (<div key={index} className='group p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 hover:bg-white/[0.04] transition-all duration-300'>
                <div className='w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-pink-500/20 flex items-center justify-center text-violet-400 mb-4 group-hover:scale-110 transition-transform duration-300'>
                  {feature.icon}
                </div>
                <h3 className='text-lg font-semibold text-white mb-2 font-outfit'>
                  {t(feature.title)}
                </h3>
                <p className='text-gray-500 text-sm leading-relaxed'>
                  {t(feature.description)}
                </p>
              </div>))}
          </div>
        </div>
      </section>

      {/* Templates Section */}
      <section className='relative py-24 px-6 lg:px-8'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex flex-col md:flex-row md:items-end md:justify-between mb-12 font-outfit'>
            <div>
              <h2 className='text-4xl lg:text-5xl font-bold mb-4'>
                {t('Ready-to-Use')}{' '}
                <span className='gradient-text'>{t('Templates')}</span>
              </h2>
              <p className='text-xl text-gray-400 max-w-xl'>
                {t('Start with pre-built workflows and customize to your needs')}
              </p>
            </div>
            <Link href='/workflows/templates' className='inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 font-medium mt-4 md:mt-0'>
              {t('View All Templates')}
              <ArrowRightIcon className='w-4 h-4'/>
            </Link>
          </div>

          <div className='grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
            {templateMetadata.map((template) => (<Link key={template.id} href={`/workflows/templates/${template.id}`} className='group relative rounded-2xl overflow-hidden bg-white/[0.02] border border-white/5 hover:border-violet-500/50 transition-all duration-300'>
                {/* Image */}
                <div className='aspect-[4/3] relative overflow-hidden'>
                  <Image src={template.backgroundImage || template.thumbnail || ''} alt={t(template.name)} fill className='object-cover group-hover:scale-110 transition-transform duration-500'/>
                  <div className='absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent'/>

                  {/* Category badge */}
                  <div className='absolute top-3 left-3 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 text-xs font-medium text-white'>
                    {t(template.category)}
                  </div>
                </div>

                {/* Content */}
                <div className='p-5'>
                  <h3 className='text-lg font-semibold text-white mb-1 font-outfit group-hover:text-violet-300 transition-colors'>
                    {t(template.name)}
                  </h3>
                  <p className='text-sm text-gray-500 mb-3'>
                    {t(template.description)}
                  </p>
                  <div className='flex items-center gap-2 text-xs text-gray-600'>
                    <span className='font-mono-jet'>
                      {template.nodeCount} {t('nodes')}
                    </span>
                  </div>
                </div>

                {/* Hover overlay */}
                <div className='absolute inset-0 bg-gradient-to-t from-violet-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none'/>
              </Link>))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className='relative py-24 px-6 lg:px-8'>
        <div className='max-w-7xl mx-auto'>
          <div className='text-center mb-16 font-outfit'>
            <h2 className='text-4xl lg:text-5xl font-bold mb-4'>
              {t('Built for')}{' '}
              <span className='gradient-text'>{t('Everyone')}</span>
            </h2>
            <p className='text-xl text-gray-400 max-w-2xl mx-auto'>
              {t('From solo creators to enterprise teams')}
            </p>
          </div>

          <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-6'>
            {useCases.map((useCase, index) => (<div key={index} className='relative group p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all duration-300 overflow-hidden'>
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${useCase.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}/>

                <div className='relative'>
                  <h3 className='text-xl font-bold text-white mb-3 font-outfit'>
                    {t(useCase.title)}
                  </h3>
                  <p className='text-gray-500 text-sm leading-relaxed'>
                    {t(useCase.description)}
                  </p>
                </div>
              </div>))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className='relative py-32 px-6 lg:px-8'>
        <div className='absolute inset-0 bg-gradient-to-b from-violet-950/30 via-pink-950/20 to-transparent'/>

        <div className='relative max-w-4xl mx-auto text-center font-outfit'>
          <h2 className='text-4xl lg:text-6xl font-bold mb-6'>
            {t('Ready to Automate Your')}
            <br />
            <span className='gradient-text'>{t('Creative Process?')}</span>
          </h2>
          <p className='text-xl text-gray-400 mb-10 max-w-2xl mx-auto'>
            {t('Join thousands of creators using AI Workflows to transform their ideas into reality.')}
          </p>

          <div className='flex flex-wrap justify-center gap-4'>
            <button onClick={handleGetStarted} className='group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl font-semibold text-lg hover:from-violet-500 hover:to-pink-500 transition-all duration-300 shadow-lg shadow-violet-500/25'>
              {t('Get Started Free')}
              <ArrowRightIcon className='w-5 h-5 group-hover:translate-x-1 transition-transform'/>
            </button>
          </div>

          <p className='text-sm text-gray-600 mt-6'>
            {t('No credit card required • Works with your existing plan')}
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className='relative py-24 px-6 lg:px-8 bg-gradient-to-b from-transparent to-black/50'>
        <div className='max-w-4xl mx-auto'>
          <div className='text-center mb-12 font-outfit'>
            <h2 className='text-4xl font-bold mb-4'>
              {t('Frequently Asked')}{' '}
              <span className='gradient-text'>{t('Questions')}</span>
            </h2>
          </div>

          <div className='space-y-4'>
            {faqs.map((faq, index) => (<details key={index} className='group p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all'>
                <summary className='flex justify-between items-center cursor-pointer list-none font-outfit font-semibold text-lg text-white'>
                  {t(faq.q)}
                  <span className='text-violet-400 group-open:rotate-45 transition-transform'>
                    +
                  </span>
                </summary>
                <p className='mt-4 text-gray-400 leading-relaxed'>{t(faq.a)}</p>
              </details>))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Video Modal */}
      {isVideoPlaying && (<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm' onClick={() => {
                setIsVideoPlaying(false);
                videoRef.current?.pause();
            }}>
          <div className='relative w-full max-w-5xl mx-4' onClick={(e) => e.stopPropagation()}>
            <button onClick={() => {
                setIsVideoPlaying(false);
                videoRef.current?.pause();
            }} className='absolute -top-12 right-0 text-white/60 hover:text-white'>
              {t('Close')}
            </button>
            <video ref={videoRef} src='https://videos.easy-peasy.ai/workflows-demo.mp4' controls autoPlay className='w-full rounded-2xl' poster='https://api.easy-peasy.ai/storage/v1/object/public/uploads/workflows/demo-poster.jpg'/>
          </div>
        </div>)}
    </main>);
}

    async function __Next_Translate__getStaticProps__1a11b03cf66__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-workflows',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a11b03cf66__ as getStaticProps }
  